import React, { useEffect, useState } from "react";
import {
  notification,
  Row,
  Col,
  Button,
  Tooltip,
  Modal,
  Input,
  Alert,
  Card,
} from "antd";
import InvoiceBillingForm from "./InvoiceBillingForm";
import InvoiceInventoryList from "./InvoiceInventoryList";
import InvoiceGrandTotal from "./InvoiceGrandTotal";
import InvoiceTotalProductionCost from "./InvoiceTotalProductionCost";
import { HiOutlineDownload } from "react-icons/hi";
import { useContext } from "react";
import mainContext from "../context/MainContext/mainContext";
import GenerateSubInvoice from "./GenerateSubInvoice";
import { useSearchParams } from "react-router-dom";
import { isRejected } from "@reduxjs/toolkit";
import InvoiceSuffixInvList from "./InvoiceSuffixInvList";
import moment from "moment";
import {
  buildInvoiceInventoriesFromBilledLines,
  computeInvoiceSubtotal,
  mapInvoiceBilledInvRows,
} from "../../../../../utils/billingInvMatch";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

const formatAckDate = (ackDate) => {
  if (!ackDate) return "";
  const m = moment(
    ackDate,
    ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DDTHH:mm:ss", moment.ISO_8601],
    true
  );
  return m.isValid() ? m.format("DD-MM-YY, hh:mm A") : ackDate;
};

const InvoiceBillingDetails = ({ invoiceId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [subTotal, setSubTotal] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isCancellingInvoice, setIsCancellingInvoice] = useState(false);
  const {
    checkAuthToken,
    cancelInvoice,
    getIrnDetails,
    invoiceData,
    setInvoiceData,
    AuthToken,
    generateEInvoice,
    remark,
    setRemark,
    cancelBillingInvoice,
  } = useContext(mainContext);
  function is48HoursPassed(dateTimeString) {
    const inputDate = new Date(dateTimeString.replace(" ", "T")); // Convert to valid ISO format
    const currentDate = new Date();

    const diffInMs = currentDate - inputDate;
    const hoursPassed = diffInMs / (1000 * 60 * 60); // convert ms to hours

    return hoursPassed > 48;
  }

  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab");
  useEffect(() => { }, [AuthToken]);
  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    if (isCancellingInvoice) {
      return;
    }
    setIsModalVisible(false);
    setRemark("");
  };

  const handleConfirm = async () => {
    if (!remark.trim() || isCancellingInvoice) {
      return;
    }
    setIsCancellingInvoice(true);
    try {
      if (!invoiceData.IRN || is48HoursPassed(invoiceData?.AckDate)) {
        await cancelBillingInvoice(invoiceId, remark);
        handleCancel();
        return;
      }
      const isCancelled = await cancelInvoice(invoiceData?.IRN);
      if (isCancelled) {
        const reqBody = {
          remark,
          IRN: invoiceData?.IRN,
        };
        const response = await fetch(
          `${apiBaseUrl}/inventoryManagement/cancelInvoice`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(reqBody),
          }
        );
        const data = await response.json();
        if (data.success == true) {
          // cancelBillingInvoice shows success + navigates; avoid duplicate toast
          await cancelBillingInvoice(invoiceId, remark);
          setInvoiceData((prevState) => ({
            ...prevState,
            isRejected: true,
          }));
        }
      }
      handleCancel();
    } finally {
      setIsCancellingInvoice(false);
    }
  };
  const fetchInvoiceDataByID = async () => {
    try {
      if (invoiceId) {
        let response = await fetch(
          `${apiBaseUrl}/inventoryManagement/getInvoiceDataById?invoiceId=${invoiceId}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        const responseData = await response.json();

        if (response.ok) {
          const invoiceInfo = responseData.invoiceInfo.salesOrderId || {};
          const bdfId = invoiceInfo?.bdfId || {};
          const rootParty = responseData?.invoiceInfo?.party || {};
          const rootBrand = responseData?.invoiceInfo?.brand || {};
          const billingPartyCompany =
            responseData?.invoiceInfo?.billingId?.partyCompany || {};
          const partyCompany = bdfId?.parentCompanyId || {};
          const billedInvs = responseData.invoiceInfo?.inventories || [];
          const billedSuffixInvs =
            responseData.invoiceInfo?.suffixInventories || [];

          // console.log("😿🟢🟢 partyCompany", partyCompany);
          setInvoiceData({
            billingId: responseData?.invoiceInfo?._id ?? null,
            HSN: responseData?.invoiceInfo?.HSN,
            IRN: responseData?.invoiceInfo?.IRN,
            refNo: responseData?.invoiceInfo?.refNo,
            refDate: responseData?.invoiceInfo?.refDate,
            bdfDate:
              responseData?.invoiceInfo?.salesOrderId?.bdfId?.bdfDate ?? null,
            isRejected: responseData?.invoiceInfo?.isRejected,
            cancelReason: responseData?.invoiceInfo?.cancelremark,
            bdfId: bdfId?._id,
            salesPerson: bdfId?.salesPerson,
            bdfStamp: bdfId?.stamp,
            partyName: rootParty?.name || null,
            partyGstNumber: rootParty?.gstNumber || null,
            partyPanNumber:
              rootParty?.panNumber ||
              billingPartyCompany?.panNumber ||
              partyCompany?.panNumber ||
              bdfId?.parentCompanyId?.panNumber ||
              bdfId?.brandId?.partyCompany?.panNumber ||
              null,
            partyState:
              rootParty?.state ||
              billingPartyCompany?.state ||
              partyCompany?.state ||
              bdfId?.parentCompanyId?.state ||
              bdfId?.brandId?.partyCompany?.state ||
              null,
            partyAddress: rootParty?.address || null,
            partyAdd1: rootParty?.add1 ?? null,
            partyAdd2: rootParty?.add2 ?? null,
            partyAdd3: rootParty?.add3 ?? null,
            partyCity: rootParty?.city ?? null,
            partyPinCode: rootParty?.pinCode ?? null,
            gstNumber:
              rootParty?.gstNumber ||
              billingPartyCompany?.gstNumber ||
              partyCompany?.gstNumber ||
              null,
            panNumber:
              rootParty?.panNumber ||
              billingPartyCompany?.panNumber ||
              partyCompany?.panNumber ||
              bdfId?.parentCompanyId?.panNumber ||
              bdfId?.brandId?.partyCompany?.panNumber ||
              null,
            state:
              rootParty?.state ||
              billingPartyCompany?.state ||
              partyCompany?.state ||
              bdfId?.parentCompanyId?.state ||
              bdfId?.brandId?.partyCompany?.state ||
              null,
            parentCompanyAddress: partyCompany?.address || null,
            parentAdd1: partyCompany?.add1 ?? null,
            parentAdd2: partyCompany?.add2 ?? null,
            parentAdd3: partyCompany?.add3 ?? null,
            parentCity: partyCompany?.city ?? null,
            parentPinCode: partyCompany?.pinCode ?? null,
            stamp: invoiceInfo.stamp || null,
            roNumber: invoiceInfo.roNumber || null,
            parentCompanyName: bdfId?.parentCompanyId?.name || bdfId?.parentCompanyName || bdfId?.brandId?.partyCompany?.name || null,
            splitParentCompanyName: billingPartyCompany?.name || null,
            splitGSTNumber: billingPartyCompany?.gstNumber || null,
            splitParentCompanyAddress: billingPartyCompany?.address || null,
            splitAdd1: billingPartyCompany?.add1 ?? null,
            splitAdd2: billingPartyCompany?.add2 ?? null,
            splitAdd3: billingPartyCompany?.add3 ?? null,
            splitCity: billingPartyCompany?.city ?? null,
            splitPinCode: billingPartyCompany?.pinCode ?? null,
            splitAmount: responseData?.invoiceInfo?.billingId?.subTotal || null,
            aliasPartyName: responseData?.invoiceInfo?.aliasPartyName || null,
            remark: bdfId?.remark || null,
            invoiceRemark:
              responseData?.invoiceInfo?.remark ||
              responseData?.invoiceInfo?.invoiceRemark ||
              null,
            invoiceSubRemark:
              responseData?.invoiceInfo?.subRemark ||
              responseData?.invoiceInfo?.invoiceSubRemark ||
              null,
            brandName:
              rootBrand?.brandName ||
              responseData?.invoiceInfo?.brandName ||
              invoiceInfo?.bdfId?.brandId?.brandName ||
              null,
            campaignName: responseData?.invoiceInfo?.campaignName || bdfId?.campaignName || null,
            agencyCommission: invoiceInfo?.agencyCommission || 0,
            campaignDuration: bdfId?.campaignDuration || null,
            bdfType: bdfId?.bdfType,
            printingCharge: responseData?.invoiceInfo?.printingCharge || 0,
            mountingCharge: responseData?.invoiceInfo?.mountingCharge || 0,
            createdAt: responseData?.invoiceInfo?.createdAt || 0,
            createdBy: responseData?.invoiceInfo?.createdBy?.name,
            bdfCreatorName: bdfId?.createdBy?.name,
            bdfCreatorId: bdfId?.createdBy?._id,
            AckDate: responseData?.invoiceInfo?.AckDate || null,
            AckNo: responseData?.invoiceInfo?.AckNo || null,
            IRN: responseData?.invoiceInfo?.IRN || null,
            billNumber: responseData?.invoiceInfo?.billNumber || null,
            billedInvs: mapInvoiceBilledInvRows(billedInvs),
            inventories: buildInvoiceInventoriesFromBilledLines(
              billedInvs,
              bdfId?.inventories,
            ),
            billedSuffixInvs:
              billedSuffixInvs?.map((inv) => ({
                _id: inv?.invId || null,
                startDate: inv?.startDate || null,
                endDate: inv?.endDate || null,
                amount: inv?.amount || null,
              })) || [],
            suffixInventories: bdfId?.suffixInventories
              ? bdfId?.suffixInventories
                .filter((inv) => {
                  // Filter suffixInventories to match those in billedSuffixInvs by their _id
                  return billedSuffixInvs.some(
                    (billedInv) => billedInv?.invId === inv?._id
                  );
                })
                .map((inv) => ({
                  _id: inv?._id,
                  specialRate: inv?.specialRate || 0,
                  purchaseRate: inv?.purchaseRate || 0,
                  suffixCode: inv?.suffixCode || null,
                  suffixInfo: inv?.suffixInfo || null,
                  startDate: inv?.startDate || null,
                  endDate: inv?.endDate || null,
                  vendor: inv?.vendor || null,
                }))
              : [],
            //taxes
            taxType: invoiceInfo?.taxType || 0,
            IGST: responseData?.invoiceInfo?.IGST || 0,
            SGST: responseData?.invoiceInfo?.SGST || 0,
            CGST: responseData?.invoiceInfo?.CGST || 0,
            municipalTax: invoiceInfo?.municipalTax || 0,
            otherTaxes: invoiceInfo?.otherTaxes || 0,
            round: invoiceInfo?.round || 0,
            isFromSub: Boolean(invoiceInfo?.isFromSub),
            isB2C: responseData?.invoiceInfo?.isB2C === true,
            isConsolidated:
              responseData?.invoiceInfo?.isConsolidated === true,
          });

          // let totalRate = bdfId?.inventories?.reduce((acc, next) => {
          //   return (
          //     acc +
          //     (Number(next?.specialRate) || 0) +
          //     (Number(next?.mountingCharge) || 0) +
          //     (Number(next?.printingCharge) || 0) +
          //     (Number(next?.innovationCharge) || 0)
          //   ); // Ensures specialRate is a number, defaults to 0 if undefined
          // }, 0);

          // if (bdfId?.suffixInventories?.length > 0) {
          //   totalRate += bdfId.suffixInventories.reduce(
          //     (acc, next) => acc + (Number(next?.specialRate) || 0),
          //     0
          //   );
          // }

          setSubTotal(
            computeInvoiceSubtotal({
              billedInvs,
              billedSuffixInvs,
              bdfInventories: bdfId?.inventories,
              printingCharge: responseData?.invoiceInfo?.printingCharge,
              mountingCharge: responseData?.invoiceInfo?.mountingCharge,
            }),
          );

          // return responseData;
        } else {
          notification.error({
            message: responseData?.message || "Failed to fetch the sales order",
          });
        }
      }
    } catch (error) {
      notification.error({
        message: error.message,
      });
    }
  };

  function setGrandTotalFunc() {
    const total =
      invoiceData?.IGST +
      invoiceData?.SGST +
      invoiceData?.CGST +
      invoiceData?.municipalTax +
      invoiceData?.otherTaxes +
      invoiceData?.round +
      invoiceData?.agencyCommission;
    let grandTotalValue = (subTotal * total) / 100 + subTotal;
    setGrandTotal(grandTotalValue);
  }

  useEffect(() => {
    setGrandTotalFunc();
  }, [invoiceData, subTotal]);

  useEffect(() => {
    fetchInvoiceDataByID();
  }, [invoiceId]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => setIsModalOpen(false);

  const updateBillingInvoiceInfo = async (partial) => {
    const billingInvoiceId = invoiceData?.billingId;
    if (!billingInvoiceId) {
      notification.error({ message: "Missing billing invoice id." });
      throw new Error("Missing billing id");
    }
    const body = { id: billingInvoiceId };
    if (partial.brandName !== undefined) {
      body.brandName = partial.brandName;
    }
    if (partial.campaignName !== undefined) {
      body.campaignName = partial.campaignName;
    }
    if (partial.remark !== undefined) {
      body.remark = partial.remark;
    }
    if (partial.subRemark !== undefined) {
      body.subRemark = partial.subRemark;
    }
    if (partial.refNo !== undefined) {
      body.refNo = partial.refNo;
    }
    if (partial.refDate !== undefined) {
      body.refDate = partial.refDate;
    }
    const response = await fetch(
      `${apiBaseUrl}/inventoryManagement/updateBillingInvoiceInfo`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }
    );
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      notification.error({
        message: responseData?.message || "Failed to update billing info",
      });
      throw new Error(responseData?.message || "Failed");
    }
    setInvoiceData((prev) => ({
      ...prev,
      ...(partial.brandName !== undefined && { brandName: partial.brandName }),
      ...(partial.campaignName !== undefined && {
        campaignName: partial.campaignName,
      }),
      ...(partial.remark !== undefined && {
        invoiceRemark: partial.remark,
      }),
      ...(partial.subRemark !== undefined && {
        invoiceSubRemark: partial.subRemark,
      }),
      ...(partial.refNo !== undefined && {
        refNo: partial.refNo,
      }),
      ...(partial.refDate !== undefined && {
        refDate: partial.refDate,
      }),
    }));
    notification.success({
      message: responseData?.message || "Saved successfully.",
    });
  };

  const isB2cInvoice = invoiceData?.isB2C === true;
  const b2cButtonStyle = {
    backgroundColor: "#fff7e6",
    borderColor: "#ffd591",
    color: "#d46b08",
  };

  return (
    <>
      <div>
        <InvoiceBillingForm
          invoiceData={invoiceData}
          tab={tab}
          onSaveBillingInfo={tab === "3" ? updateBillingInvoiceInfo : undefined}
        />

        {invoiceData?.inventories && invoiceData?.inventories?.length > 0 && (
          <InvoiceInventoryList invoiceData={invoiceData} />
        )}

        {invoiceData?.suffixInventories &&
          invoiceData?.suffixInventories?.length > 0 && (
            <InvoiceSuffixInvList invoiceData={invoiceData} />
          )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "1rem",
          }}
        >
          <div className="grandTotal-TotalProductionCost-wrapper">
            <Row>
              <Col span={12}>
                <InvoiceGrandTotal
                  invoiceData={invoiceData}
                  subTotal={subTotal}
                  grandTotal={grandTotal}
                />
              </Col>
              <Col span={12}>
                <InvoiceTotalProductionCost invoiceData={invoiceData} />
                {invoiceData?.isFromSub && invoiceData?.bdfDate && (
                  <div
                    style={{
                      marginTop: "0.75rem",
                      marginLeft: "4rem",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 12px",
                      background: "#fafafa",
                      border: "1px solid lightgrey",
                      borderRadius: "4px",
                    }}
                  >
                    <span style={{ color: "rgba(0, 0, 0, 0.65)" }}>
                      Sub BDF Date:
                    </span>
                    <span style={{ fontWeight: 600 }}>
                      {invoiceData.bdfDate}
                    </span>
                  </div>
                )}
              </Col>
            </Row>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Button
              type="primary"
              style={{
                marginTop: "2rem",
                marginRight: "1rem",
                ...(isB2cInvoice ? b2cButtonStyle : {}),
              }}
              className="billing-download-btn"
              onClick={handleOpenModal}
            >
              <HiOutlineDownload
                style={{ fontSize: "1.3rem", marginRight: "0.5rem" }}
              />
              {isB2cInvoice ? "B2C Invoice" : "Invoice"}
            </Button>

            <Tooltip
              title={
                isB2cInvoice
                  ? "B2C invoices are not submitted to the GST portal"
                  : invoiceData?.IRN
                    ? "E-Invoice already generated"
                    : ""
              }
            >
              <Button
                type="primary"
                style={{ marginTop: "2rem", marginRight: "1rem" }}
                onClick={() => {
                  Modal.confirm({
                    title: "Generate E-invoice",
                    content:
                      "Do you really want to generate the E-invoice? This will submit the invoice to the GST portal.",
                    okText: "Yes, Generate",
                    cancelText: "Cancel",
                    onOk: () =>
                      generateEInvoice(invoiceId, "INV", invoiceData),
                  });
                }}
                disabled={Boolean(invoiceData?.IRN) || isB2cInvoice}
              >
                Generate E-invoice
              </Button>
            </Tooltip>

            {/* <Tooltip title="Temporary disabled!">
              <Button
                type="primary"
                style={{ marginTop: "2rem", marginRight: "1rem" }}
                disabled={true}
              >
                Generate E-invoice
              </Button>
            </Tooltip> */}

            {invoiceData?.isRejected ? (
              <Button
                type="primary"
                style={{ marginTop: "2rem", marginRight: "1rem" }}
                disabled
              >
                Cancelled
              </Button>
            ) : (
              <Button
                type="primary"
                style={{
                  marginTop: "2rem",
                  marginRight: "1rem",
                  ...(isB2cInvoice ? b2cButtonStyle : {}),
                }}
                onClick={() => {
                  setIsModalVisible(true);
                }}
              >
                {isB2cInvoice ? "Cancel B2C Invoice" : "Cancel the Invoice"}
              </Button>
            )}

            {/* <Button
              type="primary"
              style={{ marginTop: "2rem", marginRight: "1rem" }}
              onClick={() => {
                cancelBillingInvoice(invoiceId);
              }}
            >
              Cancel Sub Invoice
            </Button> */}

            <Tooltip
              title={
                invoiceData?.IRN
                  ? "Download the E-Invoice"
                  : "First create E-Invoice"
              }
            >
              {/* <Button
                type="primary"
                style={{ marginTop: "2rem", marginRight: "1rem" }}
                disabled={invoiceData?.IRN ? false : true}
                onClick={() => {
                  getIrnDetails(invoiceData?.IRN);
                }}
              >
                Download E-Invoice
              </Button> */}
            </Tooltip>
          </div>

          <div>
            <GenerateSubInvoice
              isOpen={isModalOpen}
              onClose={handleCloseModal}
              invoiceData={invoiceData}
            />
          </div>
        </div>
      </div>
      <Modal
        title="Cancel Invoice"
        open={isModalVisible}
        onCancel={handleCancel}
        onOk={handleConfirm}
        confirmLoading={isCancellingInvoice}
        okText="Yes, Cancel"
        cancelText="No"
        disabled={isRejected}
        okButtonProps={{
          disabled: !remark.trim() || isCancellingInvoice,
        }}
        cancelButtonProps={{
          disabled: isCancellingInvoice,
        }}
      >
        <p>Please provide a remark for cancellation:</p>
        <Input.TextArea
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          placeholder="Enter cancellation reason..."
          rows={4}
        />
        {!isB2cInvoice && (
          <Alert
            message="Warning"
            description="By proceeding with this cancellation, the invoice will also be cancelled from the government portal.(If generated)"
            type="info"
            showIcon
            style={{ marginBottom: "16px", marginTop: "16px" }}
          />
        )}
        {invoiceData?.IRN && (
          <Card
            size="small"
            style={{
              marginTop: "16px",
              backgroundColor: "green",
              color: "white",
            }}
          >
            <p style={{ margin: 0 }}>
              E-invoice generated date and time:{" "}
              {formatAckDate(invoiceData?.AckDate)}
            </p>

            {is48HoursPassed(invoiceData?.AckDate) && (
              <p style={{ color: "white", marginTop: "8px" }}>
                As 48 hours have passed since the E-invoice was generated, you
                need to create a credit note for this, as it can no longer be
                cancelled.
              </p>
            )}
          </Card>
        )}
      </Modal>
    </>
  );
};

export default InvoiceBillingDetails;
